import { Injectable, OnDestroy } from '@angular/core';
import { Maintenance } from '../types/maintenance.types';
import { isSameDay, parse } from 'date-fns';
import { BehaviorSubject, Subscription, filter, switchMap, takeWhile, timer } from 'rxjs';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class MaintenanceService implements OnDestroy {
  private _hour = 60 * 3600;
  _notificationIsDismissed = false;
  _message$ = new BehaviorSubject<string | null>(null);
  private timerSubscription: Subscription | undefined;

  constructor(private api: ApiService) {
    this.checkForScheduledMaintenance();
  }

  public checkForScheduledMaintenance() {
    // Unsubscribe from previous subscription if exists
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
    // Poll the maintenance endpoint every hour
    this.timerSubscription = timer(500, this._hour)
      .pipe(
        // Stop polling is the notification is dismissed by the user.
        takeWhile(() => !this._notificationIsDismissed),
        // Do the API call
        switchMap(() => this.api.get<Maintenance>('maintenance')),
        // Only show maintenance notifcation if maintenance is planned for today.
        filter((response) => {
          if (!response?.date) {
            // in case there was maintenance planned but not anymore, make sure we hide the bar
            this._message$.next(null);
            return false;
          }
          const parsedDate = parse(response.date, 'yyyyMMdd', new Date());
          return isSameDay(parsedDate, Date.now());
        })
      )
      .subscribe({
        next: ({ message }) => {
          this._message$.next(message);
        },
        error: () => {
          this._message$.next(null);
        },
      });
  }

  get message$() {
    return this._message$;
  }

  public dismissNotification() {
    this._notificationIsDismissed = true;
    this._message$.next(null);
  }

  ngOnDestroy() {
    // Unsubscribe from the maintenance subscription when the service is destroyed
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }
}
